import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { isProductionGated } from '@/config/env';
import { footerVisibleFor } from '@/lib/homeRoute';
import { PRIVACY_POLICY_ROUTE, TERMS_ROUTE } from '@/lib/legalRoutes';

interface FooterLink {
  key: string;
  to: string;
}

/** Company / legal links (left column on desktop). Placeholders until built.
 *  Regulamin i polityka prywatności mają własne strony — do 30.09 stopka wciąż prowadziła do
 *  „wkrótce”, choć /regulamin i /polityka-prywatnosci istniały. Trasy z legalRoutes, nie literały. */
const COMPANY_LINKS: FooterLink[] = [
  { key: 'our_story', to: '/coming-soon' },
  { key: 'faq', to: '/coming-soon' },
  { key: 'terms', to: TERMS_ROUTE },
  { key: 'privacy', to: PRIVACY_POLICY_ROUTE },
  { key: 'help', to: '/coming-soon' },
];

/** Discover links — the marketing/nav entries that otherwise only appear in the
 *  logged-out top header, surfaced here so signed-in members can reach them too. */
const DISCOVER_LINKS: FooterLink[] = [
  { key: 'community', to: '/community' },
  { key: 'for_tenants', to: '/coming-soon' },
  { key: 'for_owners', to: '/coming-soon' },
];

/** SEO / discovery links (right column on desktop). */
const SEO_LINKS: FooterLink[] = [
  { key: 'seo_one_room_warsaw', to: '/coming-soon' },
  { key: 'seo_studios_warsaw', to: '/coming-soon' },
  { key: 'seo_more', to: '/coming-soon' },
];

const LinkList = ({ links }: { links: FooterLink[] }) => {
  const { t } = useTranslation();
  return (
    <ul className="list-disc space-y-2 pl-5 marker:text-muted-foreground">
      {links.map((link) => (
        <li key={link.key}>
          <Link
            to={link.to}
            className="text-sm text-foreground transition-colors hover:text-muted-foreground"
          >
            {t(`footer.${link.key}`)}
          </Link>
        </li>
      ))}
    </ul>
  );
};

/**
 * Shared site footer (both logged-in and logged-out, both actor types). Two
 * bulleted columns on desktop (company/legal + SEO); a single stacked column on
 * mobile (the mobile layout was left to our discretion in the mockup).
 */
const Footer = () => {
  // Hidden from a logged-out visitor on gated production (footerVisibleFor): its links would be
  // the only way past the coming-soon splash.
  const [hasSession, setHasSession] = useState(false);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setHasSession(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setHasSession(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);
  if (!footerVisibleFor(hasSession, isProductionGated())) return null;
  return (
  <footer className="mt-16 border-t border-border bg-muted/30">
    <div className="mx-auto max-w-7xl px-4 py-10">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        <LinkList links={COMPANY_LINKS} />
        <LinkList links={DISCOVER_LINKS} />
        <LinkList links={SEO_LINKS} />
      </div>
    </div>
  </footer>
  );
};

export default Footer;
